import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { listProducts, listCuttingTiers, listShippingMethods } from "@/lib/products";
import { getAppSetting } from "@/lib/app-settings";
import { Button } from "@/components/ui/button";
import { Mail, Phone, ArrowRight } from "lucide-react";
import { currentBrand } from "@/lib/brand";
import { useAuth } from "@/hooks/use-auth";

export const Route = createFileRoute("/pricing")({ component: PricingPage });

function PricingPage() {
  const { profile } = useAuth();
  const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: listProducts });
  const { data: cuttingTiers = [] } = useQuery({
    queryKey: ["cutting-tiers"],
    queryFn: listCuttingTiers,
  });
  const { data: shippingMethods = [] } = useQuery({
    queryKey: ["shipping-methods"],
    queryFn: listShippingMethods,
  });
  const { data: pricingAnnouncement } = useQuery({
    queryKey: ["trade-pricing-announcement"],
    queryFn: () => getAppSetting("trade_pricing_announcement"),
  });

  const backLink = profile ? "/app/help" : "/help";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Public header */}
      <header className="border-b border-border bg-background">
        <div className="mx-auto max-w-6xl flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <img
              src={currentBrand.logoSrc}
              alt={`${currentBrand.name} logo`}
              className="h-8 w-auto"
            />
            <h1 className="text-lg font-semibold">Pricing & artwork guidelines</h1>
          </div>
          <Link
            to={backLink}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Back to {profile ? "Help" : "FlightDeck"}
          </Link>
        </div>
      </header>

      <div className="flex-1 container mx-auto px-4 py-12 max-w-5xl">
        <div className="mb-12">
          <h1 className="text-4xl font-bold mb-4">Pricing & artwork guidelines</h1>
          <p className="text-lg text-muted-foreground">
            Trade pricing for Resolute customers with an active warranty or service agreement, plus
            artwork requirements for DTF and UV transfers.
          </p>
        </div>

        {pricingAnnouncement && (
          <div className="mb-8 rounded-3xl bg-gradient-hero p-6 text-primary-foreground shadow-pop">
            <p className="font-medium">{pricingAnnouncement}</p>
          </div>
        )}

        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-6">Products & pricing</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {products
              .filter((p) => p.active)
              .map((product) => (
                <div
                  key={product.id}
                  className="rounded-3xl border border-border bg-card p-6 shadow-soft"
                >
                  <h3 className="text-lg font-semibold mb-2">{product.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{product.description}</p>
                  <div className="text-lg font-bold">
                    From £{parseFloat(product.price_per_m).toFixed(2)} per metre
                  </div>
                </div>
              ))}
          </div>
          <p className="mt-6 text-sm text-muted-foreground">
            Charged per full metre only; there are no charges less than a full metre cost.
          </p>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-6">Cutting & shipping</h2>
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Cutting tiers</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {cuttingTiers
                  .filter((t) => t.active)
                  .map((tier) => (
                    <div
                      key={tier.id}
                      className="rounded-2xl border border-border bg-secondary/50 p-4"
                    >
                      <div className="font-semibold">{tier.name}</div>
                      <div className="text-sm text-muted-foreground">{tier.description}</div>
                      <div className="mt-2 font-medium">
                        £
                        {typeof tier.price_per_m === "number"
                          ? tier.price_per_m.toFixed(2)
                          : tier.price_per_m}{" "}
                        per metre
                      </div>
                    </div>
                  ))}
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Shipping methods</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {shippingMethods
                  .filter((m) => m.active)
                  .map((method) => (
                    <div
                      key={method.id}
                      className="rounded-2xl border border-border bg-secondary/50 p-4"
                    >
                      <div className="font-semibold">{method.name}</div>
                      <div className="text-sm text-muted-foreground mt-2">{method.description}</div>
                      {method.name.includes("Standard") && (
                        <div className="text-xs text-muted-foreground mt-2 italic">
                          Does not guarantee same-day print
                        </div>
                      )}
                      <div className="mt-2 font-medium">
                        £{typeof method.price === "number" ? method.price.toFixed(2) : method.price}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-6">Artwork guidelines</h2>
          <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>Transparent background required</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>2–3mm blank space around artwork</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>Correct size for printing</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>Accepted formats: Ai, EPS, PDF, PNG, PSD</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>Note: PNG compression may affect quality</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>Gang sheet rule: 56cm x 1m, no cutting if you send gang sheets</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>We do not accept files by email or compressed files (ZIP/RAR)</span>
              </li>
            </ul>
          </div>
        </section>

        <section className="rounded-3xl border border-border bg-card p-8 shadow-soft">
          <h2 className="text-2xl font-bold mb-4">Contact & support</h2>
          <p className="text-muted-foreground mb-6">
            If you need help choosing the right product or preparing artwork, call 01246 202686 and
            speak to the Trade Print Team.
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="mailto:support@resoluteink.co.uk"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-hero px-6 py-3 text-primary-foreground shadow-soft hover:opacity-90"
            >
              <Mail className="h-4 w-4" /> Contact us
            </a>
            <a
              href="tel:01246202686"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-6 py-3 hover:bg-secondary/80"
            >
              <Phone className="h-4 w-4" /> 01246 202686
            </a>
          </div>
        </section>
      </div>

      {/* Public footer */}
      <footer className="bg-sky-50 dark:bg-sky-950/20 py-8 border-t border-border">
        <div className="mx-auto max-w-6xl px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img
                src={currentBrand.logoSrc}
                alt={`${currentBrand.name} logo`}
                className="h-6 w-auto"
              />
              <span className="text-sm font-medium">{currentBrand.name}</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-foreground transition-colors">
                Home
              </Link>
              <Link to="/help" className="hover:text-foreground transition-colors">
                Help
              </Link>
              <a
                href="mailto:support@resoluteink.co.uk"
                className="flex items-center gap-1 hover:text-foreground transition-colors"
              >
                <Mail className="h-4 w-4" /> Support
              </a>
            </div>
            <p className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} {currentBrand.name}. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
